import React, { useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useCallCenterCalls } from "../../hooks/useCallCenterCalls";
import { useCallStream } from "../../hooks/useCallStream";
import { CallStatus, CallType } from "../../service/CallCenterApi";
import CallCenterNav from "../../components/call-center/CallCenterNav";
import { hasScreenAccess } from "../../utils/permissions";

const baseSchema = Yup.object({
  accountNo: Yup.string().trim().required("Account number is required"),
  phoneNumber: Yup.string().nullable(),
});

const defaultValues = {
  accountNo: "",
  phoneNumber: "",
};

const buildCallPayload = (values) => {
  const payload = {
    accountNo: values.accountNo.trim(),
  };
  const phone = (values.phoneNumber || "").trim();
  if (phone) payload.phoneNumber = phone;
  return payload;
};

const isMockTelephonyProviderId = (providerCallId) =>
  typeof providerCallId === "string" &&
  (providerCallId.startsWith("mock-freeswitch-") || providerCallId.startsWith("mock-3cx-"));

const formatCallTypeLabel = (callType) => {
  if (callType === CallType.AI_AUTOMATED) return "AI automated";
  if (callType === CallType.MANUAL) return "Manual";
  return callType ? String(callType).replace(/_/g, " ").toLowerCase() : "Outbound";
};

const isTerminalCallStatus = (status) =>
  status === CallStatus.COMPLETED || status === CallStatus.FAILED;

const isNotFoundError = (error) =>
  error?.response?.status === 404 ||
  error?.responseCode === 404 ||
  /not found/i.test(error?.message || "");

const CallCenterInboxPage = () => {
  const [callId, setCallId] = useState("");
  const [mode, setMode] = useState("manual");
  const [trackingAccountNo, setTrackingAccountNo] = useState("");
  const [completionSummary, setCompletionSummary] = useState("");
  const [completionRecordingUrl, setCompletionRecordingUrl] = useState("");
  const { createManual, triggerAI, fetchById, updateManualStatusForAccount, data, loading } = useCallCenterCalls();
  const { stateEvent, connectionState } = useCallStream(callId);

  const linkedTicketId = useMemo(
    () => data?.ticketId || stateEvent?.ticketId || "",
    [data?.ticketId, stateEvent?.ticketId]
  );

  const recordingUrl = useMemo(
    () => stateEvent?.recordingUrl || data?.recordingUrl || "",
    [stateEvent?.recordingUrl, data?.recordingUrl]
  );

  const providerCallId = useMemo(
    () => stateEvent?.providerCallId || data?.providerCallId || "",
    [stateEvent?.providerCallId, data?.providerCallId]
  );

  const showMockTelephonyHint = isMockTelephonyProviderId(providerCallId);

  const streamStatusLabel = useMemo(() => {
    switch (connectionState) {
      case "connecting":
        return "Connecting…";
      case "connected":
        return "Connected";
      case "reconnecting":
        return "Reconnecting…";
      case "closed":
        return "Closed";
      case "idle":
        return "—";
      default:
        return connectionState || "Disconnected";
    }
  }, [connectionState]);

  const canOpenTicketsList = hasScreenAccess("CC_AGENT_WORKSPACE", "canView");

  const finalCallState = stateEvent?.status || data?.status || CallStatus.INITIATED;

  const showRecordingPending = useMemo(
    () => Boolean(callId) && !recordingUrl && !isTerminalCallStatus(finalCallState),
    [callId, recordingUrl, finalCallState]
  );

  const timeline = useMemo(() => {
    const list = [];
    if (data?.status) list.push({ label: "Initial", value: data.status });
    if (stateEvent?.status) list.push({ label: "Realtime", value: stateEvent.status });
    return list;
  }, [data, stateEvent]);

  const effectiveAccountNo = useMemo(
    () => trackingAccountNo || data?.accountNo || data?.accountId || "",
    [trackingAccountNo, data?.accountNo, data?.accountId]
  );
  const canUpdateManualStatus = Boolean(effectiveAccountNo);

  const handleSubmit = async (values) => {
    try {
      const payload = buildCallPayload(values);
      const callResponse =
        mode === "manual" ? await createManual(payload) : await triggerAI(payload);

      setCallId(callResponse?.id || "");
      setTrackingAccountNo(payload.accountNo || callResponse?.accountNo || callResponse?.accountId || "");
      setCompletionSummary("");
      setCompletionRecordingUrl("");
      toast.success("Call initiated in system. Please place call manually and update status.");
    } catch (error) {
      toast.error(error.message || "Unable to initiate call");
    }
  };

  const applyManualStatus = async (status, withCompletionPayload = false) => {
    if (!effectiveAccountNo) {
      toast.info("Initiate a call first to resolve the account before updating status.");
      return;
    }

    const payload = {
      accountNo: effectiveAccountNo,
      status,
      eventTime: new Date().toISOString(),
    };

    if (withCompletionPayload) {
      const summary = completionSummary.trim();
      const recording = completionRecordingUrl.trim();
      if (summary) payload.summary = summary;
      if (recording) payload.recordingUrl = recording;
    }

    try {
      await updateManualStatusForAccount(payload);
      if (callId) {
        await fetchById(callId);
      }
      toast.success(`Call status updated to ${status}.`);
    } catch (error) {
      if (isNotFoundError(error)) {
        toast.error("Linked ticket was not found. It may have been deleted.");
        return;
      }
      toast.error(error.message || `Failed to update status to ${status}`);
    }
  };

  // Status Badge Logic
  const getStatusColor = (status) => {
    if (status === "COMPLETED") return "bg-green-100 text-green-700 border-green-200";
    if (status === "FAILED") return "bg-red-100 text-red-700 border-red-200";
    return "bg-blue-100 text-blue-700 border-blue-200";
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className=" mx-auto">
        <header className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Call Center Inbox</h2>
          <p className="text-gray-500">Initiate tracking, place calls externally, and update lifecycle status</p>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex border-b">
                <button
                  onClick={() => setMode("manual")}
                  className={`flex-1 py-4 text-sm font-medium transition-colors ${
                    mode === "manual" ? "bg-white text-[#236DB4] border-b-2 border-[#236DB4]" : "bg-gray-50 text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Manual Call
                </button>
                <button
                  onClick={() => setMode("ai")}
                  className={`flex-1 py-4 text-sm font-medium transition-colors ${
                    mode === "ai" ? "bg-white text-[#236DB4] border-b-2 border-[#236DB4]" : "bg-gray-50 text-gray-500 hover:text-gray-700"
                  }`}
                >
                  AI Triggered
                </button>
              </div>

              <Formik initialValues={defaultValues} validationSchema={baseSchema} onSubmit={handleSubmit}>
                {({ errors, touched }) => (
                  <Form className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Account number</label>
                        <Field
                          name="accountNo"
                          className={`w-full p-2.5 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all ${
                            errors.accountNo && touched.accountNo ? "border-red-500" : "border-gray-200"
                          }`}
                        />
                        {errors.accountNo && touched.accountNo && (
                          <div className="text-xs text-red-500">{errors.accountNo}</div>
                        )}
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone number</label>
                        <Field
                          name="phoneNumber"
                          className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <p className="text-xs text-gray-500">
                          Optional. Leave blank to use the mobile number stored on the account.
                        </p>
                      </div>

                      {mode === "manual" && (
                        <p className="md:col-span-2 text-sm text-gray-600">
                          A call ticket is created automatically when tracking starts. Place the call on your phone, then update status from this screen.
                        </p>
                      )}
                      {mode === "ai" && (
                        <p className="md:col-span-2 text-sm text-gray-600">
                          AI call context (outstanding balance and last updated) is taken from the account record, not this form.
                          A call ticket is created automatically when tracking starts; call lifecycle is updated manually during provider outage.
                        </p>
                      )}
                    </div>

                    <div className="mt-8 pt-6 border-t flex gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-[#236DB4] hover:bg-blue-700 text-white font-bold py-3 rounded-lg shadow-lg shadow-blue-200 disabled:opacity-50 transition-all active:transform active:scale-[0.98]"
                      >
                        {loading ? "Processing..." : "Initiate Call Now"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          {/* Right Column: Tracking & Timeline */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center justify-between">
                Live Status
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(finalCallState)}`}>
                  {finalCallState}
                </span>
              </h3>
              
              <div className="space-y-4">
                {showMockTelephonyHint && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    FreeSWITCH bridge not configured or telephony mocked — provider id is synthetic (
                    <code className="font-mono">mock-freeswitch-…</code>
                    ). Call and ticket are still valid; live events may be limited until the HTTP bridge is wired.
                  </p>
                )}

                <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                  <span className="text-xs text-gray-500 font-medium">CALL ID</span>
                  <span className="text-sm font-mono font-medium text-gray-800">{callId || "Not Started"}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                  <span className="text-xs text-gray-500 font-medium">TRACKING ACCOUNT</span>
                  <span className="text-sm font-mono font-medium text-gray-800">{effectiveAccountNo || "Not Set"}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                  <span className="text-xs text-gray-500 font-medium">STREAM</span>
                  <span
                    className={`text-sm font-medium ${
                      connectionState === "connected" ? "text-green-600" : connectionState === "reconnecting" ? "text-amber-600" : "text-gray-400"
                    }`}
                  >
                    {streamStatusLabel}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                  <span className="text-xs text-gray-500 font-medium">DURATION</span>
                  <span className="text-sm font-medium text-gray-800">{stateEvent?.duration ?? data?.duration ?? "0s"}</span>
                </div>

                {data?.callType && (
                  <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                    <span className="text-xs text-gray-500 font-medium">CALL TYPE</span>
                    <span className="text-sm font-medium text-gray-800">
                      {formatCallTypeLabel(data.callType)}
                    </span>
                  </div>
                )}

                {(data?.accountNo || data?.accountId) && (
                  <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                    <span className="text-xs text-gray-500 font-medium">ACCOUNT NO</span>
                    <span className="text-sm font-mono font-medium text-gray-800">{data.accountNo || data.accountId}</span>
                  </div>
                )}
                {data?.phoneNumber && (
                  <div className="flex justify-between items-center pb-3 border-b border-gray-50">
                    <span className="text-xs text-gray-500 font-medium">DIALED</span>
                    <span className="text-sm font-medium text-gray-800">{data.phoneNumber}</span>
                  </div>
                )}

                {linkedTicketId && (
                  <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs text-blue-700 font-bold uppercase">Call ticket</span>
                      <span className="text-sm font-bold text-blue-800 font-mono truncate" title={linkedTicketId}>
                        {linkedTicketId}
                      </span>
                    </div>
                    {canOpenTicketsList && (
                      <Link
                        to={`/call-center/tickets?ticketId=${encodeURIComponent(linkedTicketId)}`}
                        className="block text-center text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
                      >
                        View ticket in list
                      </Link>
                    )}
                  </div>
                )}

                {recordingUrl && (
                  <a
                    href={recordingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full mt-2 py-2 bg-gray-800 hover:bg-black text-white text-sm rounded-lg transition-colors"
                  >
                    <span>▶</span> Listen to Recording
                  </a>
                )}

                {showRecordingPending && (
                  <p className="text-xs text-gray-500 text-center py-2 border border-dashed border-gray-200 rounded-lg">
                    Recording pending — available after the call completes.
                  </p>
                )}

                {canUpdateManualStatus && (
                  <div className="pt-2 border-t border-gray-100 space-y-3">
                    <p className="text-xs text-gray-600">
                      Call initiated in system. Please place call manually and update status.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => applyManualStatus(CallStatus.RINGING)}
                        className="px-3 py-2 rounded-lg text-xs font-semibold border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                      >
                        Mark Ringing
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => applyManualStatus(CallStatus.ANSWERED)}
                        className="px-3 py-2 rounded-lg text-xs font-semibold border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Mark Answered
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => applyManualStatus(CallStatus.FAILED)}
                        className="px-3 py-2 rounded-lg text-xs font-semibold border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        Mark Failed
                      </button>
                    </div>

                    <div className="space-y-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-700">Complete call (optional notes)</p>
                      <input
                        type="text"
                        value={completionSummary}
                        onChange={(event) => setCompletionSummary(event.target.value)}
                        placeholder="Summary (optional)"
                        className="w-full p-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <input
                        type="url"
                        value={completionRecordingUrl}
                        onChange={(event) => setCompletionRecordingUrl(event.target.value)}
                        placeholder="Recording URL (optional)"
                        className="w-full p-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => applyManualStatus(CallStatus.COMPLETED, true)}
                        className="w-full px-3 py-2 rounded-lg text-xs font-semibold border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50"
                      >
                        Mark Completed
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {timeline.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-800 mb-4">Event History</h3>
                <div className="relative border-l-2 border-gray-100 ml-2 space-y-6">
                  {timeline.map((item, index) => (
                    <div key={`${item.label}-${index}`} className="relative pl-6">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-white border-2 border-blue-500"></div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">{item.label}</p>
                      <p className="text-sm font-medium text-gray-700">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCenterInboxPage;